import {
  useCallback,
  useEffect,
  useState
} from "react";

import * as paymentApi
  from "../../../api/payments/payments.api.js";

import {
  DEFAULT_PAYMENT_FILTERS
} from "../constants/payment.constants.js";

import {
  normalizePayment
} from "../utils/payment.utils.js";


/*
|--------------------------------------------------------------------------
| HOOK
|--------------------------------------------------------------------------
*/

export const usePayments = (
  initialFilters = {}
) => {
  /*
  |--------------------------------------------------------------------------
  | LIST STATE
  |--------------------------------------------------------------------------
  */

  const [
    payments,
    setPayments
  ] = useState([]);

  const [
    loading,
    setLoading
  ] = useState(false);

  const [
    error,
    setError
  ] = useState(null);

  const [
    filters,
    setFilters
  ] = useState({
    ...DEFAULT_PAYMENT_FILTERS,
    ...initialFilters
  });


  /*
  |--------------------------------------------------------------------------
  | SINGLE PAYMENT STATE
  |--------------------------------------------------------------------------
  */

  const [
    selectedPayment,
    setSelectedPayment
  ] = useState(null);

  const [
    selectedPaymentLoading,
    setSelectedPaymentLoading
  ] = useState(false);

  const [
    selectedPaymentError,
    setSelectedPaymentError
  ] = useState(null);


  /*
  |--------------------------------------------------------------------------
  | MUTATION STATE
  |--------------------------------------------------------------------------
  */

  const [
    saving,
    setSaving
  ] = useState(false);

  const [
    mutationError,
    setMutationError
  ] = useState(null);


  /*
  |--------------------------------------------------------------------------
  | FETCH PAYMENTS
  |--------------------------------------------------------------------------
  */

  const fetchPayments =
    useCallback(
      async (
        customFilters = filters
      ) => {
        setLoading(true);
        setError(null);


        try {
          const params = {
            ...(customFilters.status
              ? {
                  status:
                    customFilters.status
                }
              : {}),

            ...(customFilters.offerId
              ? {
                  offerId:
                    customFilters.offerId
                }
              : {})
          };


          const response =
            await paymentApi.getPayments(
              params
            );


          const data =
            response?.data?.data ??
            response?.data ??
            [];


          const normalized =
            Array.isArray(data)
              ? data.map(
                  normalizePayment
                )
              : [];


          setPayments(
            normalized
          );


          return normalized;

        } catch (err) {

          setError(err);

          throw err;

        } finally {

          setLoading(false);
        }
      },
      [filters]
    );


  /*
  |--------------------------------------------------------------------------
  | FETCH PAYMENT
  |--------------------------------------------------------------------------
  */

  const fetchPayment =
    useCallback(
      async (id) => {
        if (!id) {
          return null;
        }


        setSelectedPaymentLoading(
          true
        );

        setSelectedPaymentError(
          null
        );


        try {
          const response =
            await paymentApi.getPayment(
              id
            );


          const data =
            response?.data?.data ??
            response?.data ??
            null;


          const normalized =
            data
              ? normalizePayment(data)
              : null;


          setSelectedPayment(
            normalized
          );


          return normalized;

        } catch (err) {

          setSelectedPaymentError(
            err
          );

          throw err;

        } finally {

          setSelectedPaymentLoading(
            false
          );
        }
      },
      []
    );


  /*
  |--------------------------------------------------------------------------
  | CREATE PAYMENT ORDER
  |--------------------------------------------------------------------------
  */

  const createPaymentOrder =
    useCallback(
      async (
        offerId,
        gateway
      ) => {
        setSaving(true);
        setMutationError(null);


        try {
          const response =
            await paymentApi.createPaymentOrder(
              offerId,
              gateway
            );


          return response;

        } catch (err) {

          setMutationError(
            err
          );

          throw err;

        } finally {

          setSaving(false);
        }
      },
      []
    );


  /*
  |--------------------------------------------------------------------------
  | VERIFY PAYMENT
  |--------------------------------------------------------------------------
  */

  const verifyPayment =
    useCallback(
      async (payload) => {
        setSaving(true);
        setMutationError(null);


        try {
          const response =
            await paymentApi.verifyPayment(
              payload
            );


          const data =
            response?.data?.data ??
            response?.data ??
            null;


          const normalized =
            data
              ? normalizePayment(data)
              : null;


          if (normalized) {
            setSelectedPayment(
              normalized
            );
          }


          await fetchPayments();


          return normalized;

        } catch (err) {

          setMutationError(
            err
          );

          throw err;

        } finally {

          setSaving(false);
        }
      },
      [fetchPayments]
    );


  /*
  |--------------------------------------------------------------------------
  | FILTERS
  |--------------------------------------------------------------------------
  */

  const updateFilters =
    useCallback(
      (updates) => {
        setFilters(
          (current) => ({
            ...current,
            ...updates
          })
        );
      },
      []
    );


  const resetFilters =
    useCallback(
      () => {
        setFilters({
          ...DEFAULT_PAYMENT_FILTERS
        });
      },
      []
    );


  /*
  |--------------------------------------------------------------------------
  | CLEAR ERRORS
  |--------------------------------------------------------------------------
  */

  const clearErrors =
    useCallback(
      () => {
        setError(null);
        setSelectedPaymentError(null);
        setMutationError(null);
      },
      []
    );


  /*
  |--------------------------------------------------------------------------
  | INITIAL LOAD
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);


  /*
  |--------------------------------------------------------------------------
  | RETURN
  |--------------------------------------------------------------------------
  */

  return {
    /*
    |----------------------------------------------------------------------
    | DATA
    |----------------------------------------------------------------------
    */

    payments,
    selectedPayment,

    /*
    |----------------------------------------------------------------------
    | LIST
    |----------------------------------------------------------------------
    */

    filters,
    updateFilters,
    resetFilters,
    fetchPayments,

    /*
    |----------------------------------------------------------------------
    | SINGLE
    |----------------------------------------------------------------------
    */

    fetchPayment,
    setSelectedPayment,

    /*
    |----------------------------------------------------------------------
    | PAYMENT
    |----------------------------------------------------------------------
    */

    createPaymentOrder,
    verifyPayment,

    /*
    |----------------------------------------------------------------------
    | LOADING
    |----------------------------------------------------------------------
    */

    loading,
    selectedPaymentLoading,
    saving,

    /*
    |----------------------------------------------------------------------
    | ERRORS
    |----------------------------------------------------------------------
    */

    error,
    selectedPaymentError,
    mutationError,

    clearErrors
  };
};
